import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { adminApi, clearSession, getAdminEmail, formatApiError } from "@/lib/auth";
import { BLOG_PATH } from "@/lib/blogConfig";
import {
  Plus,
  LogOut,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  Loader2,
} from "lucide-react";

export default function Admin() {
  const nav = useNavigate();
  const loc = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.get("/admin/posts");
      setPosts(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [loc.key, load]);

  const togglePublish = async (p) => {
    try {
      await adminApi.put(`/admin/posts/${p.id}`, { published: !p.published });
      load();
    } catch (e) {
      alert(formatApiError(e?.response?.data?.detail));
    }
  };

  const deletePost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/posts/${p.id}`);
      load();
    } catch (e) {
      alert(formatApiError(e?.response?.data?.detail));
    }
  };

  const logout = () => {
    clearSession();
    nav("/admin/login", { replace: true });
  };

  return (
    <main data-testid="admin-page" className="bg-bone min-h-screen">
      {/* Top bar */}
      <header className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/admin" className="font-display text-xl tracking-tight">
            J<span className="text-brass">.</span>J<span className="text-brass">.</span> Botelho —{" "}
            <span className="text-brass">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-white/60 text-sm">
              {getAdminEmail()}
            </span>
            <Link
              to="/admin/new"
              data-testid="admin-new-post-btn"
              className="inline-flex items-center gap-2 bg-brass text-ink px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-white transition-colors"
            >
              <Plus className="w-4 h-4" />
              New post
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="overline text-ink">Manage Posts</p>
            <h1 className="mt-3 font-display text-ink text-3xl sm:text-4xl tracking-tight">
              Your blog dashboard
            </h1>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 mb-6 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-muted2 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading posts…
          </p>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border2 rounded-2xl">
            <h2 className="font-display text-ink text-2xl">No posts yet.</h2>
            <p className="mt-3 text-text2/80">
              Upload your first blog PDF to get started.
            </p>
            <Link
              to="/admin/new"
              className="mt-6 inline-flex items-center gap-2 bg-ink text-white px-6 py-3 rounded-full text-sm font-semibold"
            >
              <Plus className="w-4 h-4" />
              Upload first post
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-border2 rounded-2xl overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-sand text-xs uppercase tracking-widest text-muted2">
                <tr>
                  <th className="px-6 py-4">Title</th>
                  <th className="px-6 py-4 hidden md:table-cell">Category</th>
                  <th className="px-6 py-4 hidden lg:table-cell">Updated</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border2">
                {posts.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`admin-row-${p.slug}`}
                    className="hover:bg-bone/60"
                  >
                    <td className="px-6 py-4">
                      <div className="font-display text-ink text-base leading-tight">
                        {p.title}
                      </div>
                      <div className="text-muted2 text-xs mt-1">/{p.slug}</div>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell text-text2">
                      {p.category}
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell text-text2 text-xs">
                      {new Date(p.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                          p.published
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-amber-50 text-amber-700"
                        }`}
                      >
                        {p.published ? "Live" : "Draft"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-3 text-muted2">
                        {p.published && (
                          <a
                            href={`${BLOG_PATH}/${p.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View"
                            data-testid={`admin-view-${p.slug}`}
                            className="hover:text-ink"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          onClick={() => togglePublish(p)}
                          title={p.published ? "Unpublish" : "Publish"}
                          data-testid={`admin-toggle-${p.slug}`}
                          className="hover:text-ink"
                        >
                          {p.published ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                        <Link
                          to={`/admin/edit/${p.id}`}
                          title="Edit"
                          data-testid={`admin-edit-${p.slug}`}
                          className="hover:text-ink"
                        >
                          <Pencil className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => deletePost(p)}
                          title="Delete"
                          data-testid={`admin-delete-${p.slug}`}
                          className="hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
